import React from 'react';
import classnames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import Pipeline from 'types/pipeline';
import { PipelineParams } from 'types/route_params';

import { useAppRedirect } from 'hooks/useAppRedirect';
import { useDeletePipelines } from 'pipelines/hooks/useDeletePipelines';
import { useModal } from 'hooks/useModal';
import { usePipeline } from 'pipelines/hooks/usePipeline';
import { useUpdatePipeline } from 'pipelines/hooks/useUpdatePipeline';

import * as PageLayout from 'components/PageLayout';
import { Loader } from 'components/Loader/Loader';
import { NotFound } from 'views/NotFound/NotFound';

import EmptyIllustration from 'pipelines/images/empty-pipelines.svg?react';
import { PipelineDetailProvider } from 'pipelines/context/pipeline_detail_view';
import { PipelineEditor } from 'pipelines/components/Editor/Editor';
import { PipelineDetailError } from './Error/Error';

import styles from './PipelineDetail.module.scss';

const c = classnames.bind(styles);

export function PipelineDetail() {
  const redirect = useAppRedirect();
  const { open } = useModal();

  const { pipelineID } = useParams<PipelineParams>();
  const { data: initialPipeline, isLoading, refetch } = usePipeline(pipelineID);

  const [key, setKey] = React.useState(0);

  // Listen for pipeline updates from Copilot frontend tools
  React.useEffect(() => {
    function handlePipelineUpdated(event: CustomEvent<{ id: string }>) {
      if (event.detail.id === pipelineID) {
        refetch().then(() => {
          setKey((previousKey) => previousKey + 1);
        });
      }
    }

    window.addEventListener(
      'pipeline:updated',
      handlePipelineUpdated as EventListener
    );
    return () => {
      window.removeEventListener(
        'pipeline:updated',
        handlePipelineUpdated as EventListener
      );
    };
  }, [pipelineID, refetch]);

  const { mutateAsync: deletePipelines, isLoading: isDeleting } =
    useDeletePipelines({
      onSuccess: () => redirect('/design/pipelines'),
    });

  const { mutate: restorePipeline, isLoading: isRestoring } = useUpdatePipeline(
    {
      onSuccess() {
        setKey((previousKey) => previousKey + 1);
      },
    }
  );

  function handleRestorePipeline() {
    if (!initialPipeline) {
      return;
    }

    const updatedPipeline = Pipeline.withInvalidNodesRemoved(initialPipeline);
    restorePipeline({
      id: updatedPipeline.id,
      definition: updatedPipeline.definition,
    });
  }

  function handleDeletePipeline() {
    if (!initialPipeline) {
      return;
    }

    open('confirm-delete', {
      title: `Are you sure you want to delete ${initialPipeline.name}?`,
      onConfirm: () => deletePipelines({ pipeline_ids: [initialPipeline.id] }),
    });
  }

  if (!initialPipeline && isLoading) {
    return <Loader text="Loading workflow..." />;
  }

  if (!initialPipeline) {
    return (
      <NotFound
        entity="pipeline"
        returnTo="/design/pipelines"
        image={EmptyIllustration}
      />
    );
  }

  return (
    <PipelineDetailProvider pipeline={initialPipeline} key={key}>
      <PageLayout.Root size="full">
        <PageLayout.Content>
          <div className={c('pipeline')}>
            <div className={c('rete')}>
              <ErrorBoundary
                fallbackRender={({ resetErrorBoundary }) => (
                  <PipelineDetailError
                    isRestoring={isRestoring}
                    isDeleting={isDeleting}
                    resetErrorBoundary={resetErrorBoundary}
                    onRestore={handleRestorePipeline}
                    onDelete={handleDeletePipeline}
                  />
                )}
              >
                <PipelineEditor />
              </ErrorBoundary>
            </div>
          </div>
        </PageLayout.Content>
      </PageLayout.Root>
    </PipelineDetailProvider>
  );
}
